import clsx from 'clsx'
import { defineComponent } from 'vue'

import VButton from '@/components/common/button/VButton'
import SelectDate from '@/components/common/date/SelectDate'
import VTextarea from '@/components/common/text-field/VTextarea'
import VTextField from '@/components/common/text-field/VTextField'
import VDropdown, { type DropdownItem } from '@/components/common/VDropdown'
import VForm from '@/components/common/VForm'
import VLabelCheckbox from '@/components/common/VLabelCheckbox'
import VStepper from '@/components/common/VStepper'
import AdmissionRoomForm from '@/components/views/booking/admission-room-form/AdmissionRoomForm'
import useBreakpoints from '@/composables/common/useBreakpoints'
import useLocalization from '@/composables/localization/useLocalization'
import useBookingView from '@/composables/views/booking/useBookingView'

export default defineComponent(() => {
  const { localeTexts, localizationNameKey } = useLocalization()
  const { isMobile } = useBreakpoints()
  const {
    allServicesQuery,
    bookingVisitTimesQuery,
    allDoctors,
    bookingSteps,
    bookingCompletedStep,
    serviceSelected,
    dateSelected,
    timeSelected,
    symptoms,
    doctorSelected,
    isRequireRoomForAdmission,
    firstName,
    lastName,
    email,
    phone,
    insurance,
    policyNumber,
    isVisitDetailsCompleted,
    isPatientDetailsCompleted,
    isPatientVip,
    setBookingState,
    setServiceSelected,
    setDateSelected,
    setTimeSelected,
    setSymptoms,
    setDoctorSelected,
    setIsRequireRoomForAdmission,
    setFirstName,
    setLastName,
    setEmail,
    setPhone,
    setInsurance,
    setPolicyNumber,
    setIsPatientVip
  } = useBookingView()

  return () => (
    <VForm
      t-data='booking-form'
      class='w-full'
      onSubmit={() => { setBookingState('confirmation') }}
    >
      <div class='mt-6'>
        <h3 class='text-heading-5'>{localeTexts.value['create-booking-step1-visit-details-heading']}</h3>
        <div class='mt-2 text-body-1'>{localeTexts.value['create-booking-step1-visit-details-paragraph']}</div>
        <div class='mt-6 space-y-8'>
          <VDropdown
            t-data='booking-service-dropdown'
            label={localeTexts.value['create-booking-step1-service-label']}
            required
            variant='floating'
            items={allServicesQuery.data.value}
            itemKey='id'
            itemText={localizationNameKey.value}
            value={serviceSelected.value}
            highlightEnabled
            isMobileMode={isMobile.value}
            onInput={setServiceSelected}
          />
          <div class='flex flex-col gap-x-6 gap-y-8 tablet:flex-row'>
            <SelectDate
              t-data='booking-visit-date-select-date'
              class='flex-1'
              label={localeTexts.value['create-booking-step1-visit-date-label']}
              required
              variant='floating'
              placeholder={localeTexts.value['create-booking-step1-visit-date-placeholder']}
              value={dateSelected.value}
              isMobileMode={isMobile.value}
              onChange={setDateSelected}
            />
            <VDropdown
              t-data='booking-visit-time-dropdown'
              class='flex-1'
              label={localeTexts.value['create-booking-step1-visit-time-label']}
              required
              variant='floating'
              placeholder={localeTexts.value['create-booking-step1-visit-time-placeholder']}
              items={bookingVisitTimesQuery.data.value}
              itemKey='id'
              itemText='time_range'
              value={timeSelected.value}
              highlightEnabled
              isMobileMode={isMobile.value}
              onInput={setTimeSelected}
            />
          </div>
          <VTextarea
            t-data='booking-symptomse-textarea'
            inputClass='min-h-24'
            label={localeTexts.value['create-booking-step1-symptoms-label']}
            variant='floating'
            value={symptoms.value}
            onInput={setSymptoms}
          />
          <VDropdown
            t-data='booking-doctor-dropdown'
            label={localeTexts.value['create-booking-step1-doctor-label']}
            variant='floating'
            items={allDoctors.value}
            itemKey='id'
            itemText='name_th'
            value={doctorSelected.value}
            highlightEnabled
            isMobileMode={isMobile.value}
            onInput={setDoctorSelected}
          >
            {{
              item ({ itemText, item, index }: DropdownItem) {
                return (
                  <>
                    {index !== 0 &&
                      <img
                        class='size-10 object-cover'
                        src={item.image_url}
                      />}
                    <span>{itemText}</span>
                  </>
                )
              }
            }}
          </VDropdown>
          <VLabelCheckbox
            t-data='booking-require-room-for-admission-checkbox'
            label={localeTexts.value['create-booking-step1-room-checkbox']}
            value={isRequireRoomForAdmission.value}
            onInput={setIsRequireRoomForAdmission}
          />
        </div>
        <div class={clsx(
          'mt-2 overflow-y-hidden transition-all duration-500',
          {
            'max-h-0': !isRequireRoomForAdmission.value,
            'max-h-[1000px]': isRequireRoomForAdmission.value
          })}
        >
          <AdmissionRoomForm />
        </div>
      </div>
      <div class='mt-10'>
        <h3 class='text-heading-5'>{localeTexts.value['create-booking-step2-patient-details-heading']}</h3>
        <div class='mt-2 text-body-1'>{localeTexts.value['create-booking-step2-patient-details-paragraph']}</div>
        <div class='mt-6 grid grid-cols-1 gap-x-6 gap-y-8 tablet:grid-cols-2'>
          <VTextField
            t-data='booking-first-name-text-field'
            type='text'
            label={localeTexts.value['create-booking-step2-firstname-label']}
            name='firstname'
            required
            variant='floating'
            value={firstName.value}
            onInput={setFirstName}
          />
          <VTextField
            t-data='booking-last-name-text-field'
            type='text'
            label={localeTexts.value['create-booking-step2-lastname-label']}
            name='lastname'
            required
            variant='floating'
            value={lastName.value}
            onInput={setLastName}
          />
          <VTextField
            t-data='booking-email-text-field'
            type='email'
            label={localeTexts.value['create-booking-step2-email-label']}
            name='email'
            variant='floating'
            value={email.value}
            onInput={setEmail}
          />
          <VTextField
            t-data='booking-mobile-text-field'
            type='tel'
            label={localeTexts.value['create-booking-step2-mobile-label']}
            name='mobile'
            required
            variant='floating'
            pattern='^0\d{9}$'
            value={phone.value}
            onInput={setPhone}
          />
          <VTextField
            t-data='booking-insurance-text-field'
            type='text'
            label={localeTexts.value['create-booking-step2-insurance-label']}
            name='insurance'
            variant='floating'
            value={insurance.value}
            onInput={setInsurance}
          />
          <VTextField
            t-data='booking-policy-number-text-field'
            type='text'
            label={localeTexts.value['create-booking-step2-policy-number-label']}
            name='policynumber'
            variant='floating'
            value={policyNumber.value}
            onInput={setPolicyNumber}
          />
          <VLabelCheckbox
            t-data='booking-vip-checkbox'
            label={localeTexts.value['create-booking-step2-vip-checkbox']}
            value={isPatientVip.value}
            onInput={setIsPatientVip}
          />
        </div>
      </div>
      <div class='fixed bottom-0 left-0 z-999 w-full border-t border-neutral-300 bg-white'>
        <div class='mx-auto flex w-full flex-col items-center gap-3 p-6 tablet:flex-row desktop:w-main-content desktop:px-0'>
          <div class='w-full flex-1 tablet:w-auto'>
            <VStepper
              t-data='booking-form-stepper'
              items={bookingSteps.value}
              value={bookingCompletedStep.value}
              isFull={isMobile.value}
            />
          </div>
          <div class='w-full flex-none tablet:w-auto'>
            <VButton
              t-data='booking-form-next-btn'
              class='w-full tablet:w-auto'
              type='submit'
              disabled={!isVisitDetailsCompleted.value || !isPatientDetailsCompleted.value}
            >
              {localeTexts.value['create-booking-next-btn']}
            </VButton>
          </div>
        </div>
      </div>
    </VForm>
  )
}, {
  name: 'BookingForm'
})
